import React, { FC } from 'react';
import {
    Photo as ExistingPhotoFields,
    CopyrightType,
} from '@/generated/graphql';
import { DEFAULT_COPYRIGHT_NID } from '@/store';
import Input from '@/components/basic/Input/Input';
import Select from '@/components/basic/Select/Select';
import ImageUpload from '@/components/edit/media/ImageUpload/ImageUpload';
import CopyrightInput from '@/components/edit/media/CopyrightInput/CopyrightInput';
export interface PhotoData {
    caption?: string;
    imageFile?: File | null;
    align?: 'left' | 'right';
    copyright?: number;
    copyrightUrl?: string;
    newCopyrightName?: string;
    newCopyrightType?: CopyrightType;
    newCopyrightUrl?: string;
}
export interface PhotoWrapper {
    photo: PhotoData;
}

export const getInitialPhotoData = (): PhotoWrapper => ({
    photo: {
        copyright: DEFAULT_COPYRIGHT_NID,
        caption: '',
        imageFile: null,
    },
});
interface PhotoInputProps {
    photoPurpose?: 'photo' | 'animation';
    value: PhotoData;
    onChange: (updatedPhoto: PhotoData) => void;
    showAlign?: boolean;
    existingPhotoFields?: ExistingPhotoFields & { id?: number };
}

const PhotoInput: FC<PhotoInputProps> = ({
    photoPurpose = 'photo',
    value,
    onChange,
    showAlign = false,
    existingPhotoFields,
}) => {
    const handleChange = <K extends keyof PhotoData>(
        field: K,
        fieldValue: PhotoData[K],
    ) => {
        onChange({ ...value, [field]: fieldValue });
    };

    const handleCopyrightChange = (updatedFields: Partial<PhotoData>) => {
        onChange({ ...value, ...updatedFields });
    };

    const isAnimation = photoPurpose === 'animation';
    const imageConfig = {
        label: isAnimation ? 'Beschreibung' : 'Bildunterschrift',
        placeholder: isAnimation
            ? 'Kurzbeschreibung der Animation'
            : 'Kurzbeschreibung des Bildes',
        acceptMimeType: isAnimation
            ? ['image/jpeg']
            : ['image/png', 'image/jpeg'],
    };

    return (
        <>
            <Input
                label={imageConfig.label}
                placeholder={imageConfig.placeholder}
                value={value.caption ?? ''}
                type="text"
                required
                labelWidth
                onChange={(e) => handleChange('caption', e.target.value)}
            />
            <CopyrightInput value={value} onChange={handleCopyrightChange} />

            {showAlign && (
                <Select
                    label="Ausrichten"
                    options={[
                        { value: 'left', label: 'Links' },
                        { value: 'right', label: 'Rechts' },
                    ]}
                    required
                    labelWidth
                    value={value.align ?? 'left'}
                    onChange={(e) =>
                        handleChange(
                            'align',
                            e.target.value as 'left' | 'right',
                        )
                    }
                />
            )}

            <ImageUpload
                accept={imageConfig.acceptMimeType}
                required
                labelWidth
                existingPhotoFields={existingPhotoFields}
                photoPurpose={photoPurpose}
                onChangeCallback={(file) => handleChange('imageFile', file)}
            />
        </>
    );
};

export default PhotoInput;
